/**
 * «Исследователь метрик» — API-клиент: `/business-web/catalog`, `/business-web/context`, `/business-web/query`.
 * Гейтвей отдаёт `{ success, data }`, ошибка — `{ success:false, error:{ code, message, retryAfterSeconds? } }`.
 * Доступ — как у MCP «Бизнес» (BusinessMcp:READ + кабинеты пользователя), а не SalesAnalytics.
 */

import axios from 'axios'
import { api } from '../../shared/api/client'
import { unwrap } from '../analytics/shared/api'
import type {
  ExplorerArea,
  ExplorerCatalog,
  ExplorerContext,
  ExplorerDimension,
  ExplorerDimensionKind,
  ExplorerFact,
  ExplorerMarketplace,
  ExplorerMetric,
  ExplorerMetricKind,
  ExplorerQueryBody,
  ExplorerResult,
  ExplorerRow,
  ExplorerTimeGrain,
} from './types'

const text = (value: unknown): string => (value === null || value === undefined ? '' : String(value))
const strings = (value: unknown): string[] => (Array.isArray(value) ? value.map(text).filter(Boolean) : [])
const list = (value: unknown): any[] => (Array.isArray(value) ? value : [])
const num = (value: unknown, fallback: number): number => {
  const result = Number(value)
  return Number.isFinite(result) ? result : fallback
}
const nullableText = (value: unknown): string | null => (value === null || value === undefined ? null : String(value))

/** Ошибка вызова гейтвея: код сервера, понятный текст и пауза повтора (если сервер занят). */
export class ExplorerApiError extends Error {
  readonly code: string
  readonly status: number | null
  readonly retryAfterSeconds: number | null

  constructor(message: string, code = 'UNKNOWN', status: number | null = null, retryAfterSeconds: number | null = null) {
    super(message)
    this.name = 'ExplorerApiError'
    this.code = code
    this.status = status
    this.retryAfterSeconds = retryAfterSeconds
  }
}

export const CAPACITY_ERROR_CODE = 'BUSINESS_QUERY_CAPACITY'

/** Приводит ошибку axios к ExplorerApiError; отмену запроса не трогает (её отличает `isAbortError`). */
export function toExplorerError(error: unknown): unknown {
  if (error instanceof ExplorerApiError || axios.isCancel(error)) return error
  const source = error as any
  const body = source?.response?.data?.error ?? {}
  const status = Number(source?.response?.status)
  const retry = Number(body.retryAfterSeconds ?? source?.response?.headers?.['retry-after'])
  const code = text(body.code) || (status === 403 ? 'FORBIDDEN' : 'UNKNOWN')
  const message = text(body.message) || text(source?.response?.data?.message) || text(source?.message) || 'Не удалось получить данные'
  return new ExplorerApiError(message, code, Number.isFinite(status) ? status : null, Number.isFinite(retry) && retry > 0 ? retry : null)
}

export const isAbortError = (error: unknown): boolean => axios.isCancel(error) || (error as any)?.name === 'CanceledError'

/** Текст ошибки для экрана: 403 — как в ответе, «сервер занят» — с паузой, остальное — сообщение сервера. */
export function explorerErrorText(error: unknown): string {
  const normalized = toExplorerError(error)
  if (!(normalized instanceof ExplorerApiError)) return 'Не удалось получить данные'
  if (normalized.code === CAPACITY_ERROR_CODE) {
    return normalized.retryAfterSeconds ? `Сервер занят, повторите через ${normalized.retryAfterSeconds} с` : 'Сервер занят, повторите позже'
  }
  if (normalized.status === 401) return 'Сессия истекла. Войдите заново.'
  return normalized.message
}

const KINDS: ExplorerMetricKind[] = ['flow', 'state', 'ratio']
const DIM_KINDS: ExplorerDimensionKind[] = ['time', 'seller', 'entity', 'category']
const GRAINS: ExplorerTimeGrain[] = ['day', 'week', 'month', 'quarter', 'year']

function normalizeArea(raw: any): ExplorerArea | null {
  const id = text(raw?.id)
  return id ? { id, label: text(raw?.label) || id, factIds: strings(raw?.factIds) } : null
}

function normalizeFact(raw: any): ExplorerFact | null {
  const id = text(raw?.id)
  if (!id) return null
  const marketplaces = strings(raw?.marketplaces).filter((item): item is ExplorerMarketplace => item === 'wb' || item === 'ozon')
  return {
    id,
    label: text(raw?.label) || id,
    areaId: text(raw?.areaId),
    marketplaces: marketplaces.length > 0 ? marketplaces : ['wb', 'ozon'],
    dateBasis: strings(raw?.dateBasis),
    dimensionIds: strings(raw?.dimensionIds),
    limitations: strings(raw?.limitations),
  }
}

function normalizeDimension(raw: any): ExplorerDimension | null {
  const id = text(raw?.id)
  if (!id) return null
  const grain = GRAINS.includes(raw?.timeGrain) ? (raw.timeGrain as ExplorerTimeGrain) : undefined
  return {
    id,
    label: text(raw?.label) || id,
    meaning: text(raw?.meaning),
    kind: DIM_KINDS.includes(raw?.kind) ? (raw.kind as ExplorerDimensionKind) : 'entity',
    ...(grain ? { timeGrain: grain } : {}),
  }
}

function normalizeMetric(raw: any): ExplorerMetric | null {
  const id = text(raw?.id)
  if (!id) return null
  const requires = strings(raw?.requires)
  return {
    id,
    label: text(raw?.label) || id,
    factId: raw?.factId ? text(raw.factId) : null,
    unit: text(raw?.unit),
    aggregation: text(raw?.aggregation),
    kind: KINDS.includes(raw?.kind) ? (raw.kind as ExplorerMetricKind) : 'flow',
    dateBasis: text(raw?.dateBasis),
    formula: text(raw?.formula),
    ...(requires.length > 0 ? { requires } : {}),
    accessArea: text(raw?.accessArea),
  }
}

const compact = <T>(items: Array<T | null>): T[] => items.filter((item): item is T => item !== null)

export function normalizeCatalog(payload: any): ExplorerCatalog {
  return {
    semanticVersion: text(payload?.semanticVersion),
    limits: {
      metrics: num(payload?.limits?.metrics, 10),
      dimensions: num(payload?.limits?.dimensions, 5),
      pageSize: num(payload?.limits?.pageSize, 500),
      periodYears: num(payload?.limits?.periodYears, 3),
    },
    areas: compact(list(payload?.areas).map(normalizeArea)),
    facts: compact(list(payload?.facts).map(normalizeFact)),
    dimensions: compact(list(payload?.dimensions).map(normalizeDimension)),
    metrics: compact(list(payload?.metrics).map(normalizeMetric)),
  }
}

export function normalizeContext(payload: any): ExplorerContext {
  const sellers = compact(
    list(payload?.sellers).map((raw) => {
      const marketplace = raw?.marketplace === 'ozon' ? 'ozon' : raw?.marketplace === 'wb' ? 'wb' : null
      const sellerId = Number(raw?.sellerId)
      if (!marketplace || !Number.isFinite(sellerId)) return null
      return {
        marketplace,
        sellerId,
        ...(raw?.name ? { name: text(raw.name) } : {}),
        ...(raw?.shortName ? { shortName: text(raw.shortName) } : {}),
        ...(raw?.cabinetCode ? { cabinetCode: text(raw.cabinetCode) } : {}),
      } as ExplorerContext['sellers'][number]
    }),
  )
  return { sellers, semanticVersion: text(payload?.semanticVersion) }
}

function normalizeRow(raw: any): ExplorerRow {
  const dimensions: ExplorerRow['dimensions'] = {}
  for (const [key, value] of Object.entries(raw?.dimensions ?? {})) dimensions[key] = nullableText(value)
  const values: ExplorerRow['values'] = {}
  for (const [key, value] of Object.entries(raw?.values ?? {})) values[key] = nullableText(value)
  const metricQuality: ExplorerRow['metricQuality'] = {}
  for (const [key, value] of Object.entries(raw?.metricQuality ?? {})) metricQuality[key] = text(value)
  return { dimensions, values, metricQuality }
}

export function normalizeResult(payload: any): ExplorerResult {
  const totals: ExplorerResult['totals'] = {}
  for (const [key, value] of Object.entries(payload?.totals ?? {})) totals[key] = nullableText(value)
  const metricQuality: ExplorerResult['metricQuality'] = {}
  for (const [key, value] of Object.entries(payload?.metricQuality ?? {})) metricQuality[key] = text(value)
  const projectionMetadata: ExplorerResult['projectionMetadata'] = {}
  for (const [key, value] of Object.entries(payload?.projectionMetadata ?? {})) {
    const meta = value as any
    projectionMetadata[key] = { sourceFrom: meta?.sourceFrom ? text(meta.sourceFrom) : undefined, sourceTo: meta?.sourceTo ? text(meta.sourceTo) : undefined, loadedAt: meta?.loadedAt ? text(meta.loadedAt) : undefined }
  }
  const rows = list(payload?.rows).map(normalizeRow)
  return {
    rows,
    totals,
    metricQuality,
    totalRows: num(payload?.totalRows, rows.length),
    hasMore: Boolean(payload?.hasMore),
    metrics: list(payload?.metrics).map((metric) => ({
      id: text(metric?.id),
      label: text(metric?.label) || text(metric?.id),
      source: text(metric?.source),
      unit: text(metric?.unit),
      aggregation: text(metric?.aggregation),
      dateBasis: text(metric?.dateBasis),
      formula: text(metric?.formula),
    })),
    quality: {
      state: text(payload?.quality?.state),
      sources: list(payload?.quality?.sources).map((source) => ({
        source: text(source?.source),
        quality: { state: text(source?.quality?.state), issues: strings(source?.quality?.issues) },
      })),
      issues: list(payload?.quality?.issues).map((issue) => ({
        metricId: text(issue?.metricId),
        code: text(issue?.code),
        ...(issue?.row !== undefined && issue?.row !== null ? { row: num(issue.row, 0) } : {}),
      })),
      sourceIssues: strings(payload?.quality?.sourceIssues),
    },
    limitations: strings(payload?.limitations),
    entityLabels: {
      categories: list(payload?.entityLabels?.categories)
        .map((item) => ({ categoryId: Number(item?.categoryId), label: text(item?.label) }))
        .filter((item) => Number.isFinite(item.categoryId) && item.label),
      sellers: list(payload?.entityLabels?.sellers)
        .map((item) => ({ marketplace: text(item?.marketplace), sellerId: Number(item?.sellerId), label: text(item?.label) }))
        .filter((item) => Number.isFinite(item.sellerId) && item.label),
    },
    period: {
      from: text(payload?.period?.from),
      to: text(payload?.period?.to),
      timezone: text(payload?.period?.timezone) || 'Europe/Moscow',
    },
    sellers: list(payload?.sellers)
      .map((item) => ({ marketplace: item?.marketplace === 'ozon' ? ('ozon' as const) : ('wb' as const), sellerId: Number(item?.sellerId) }))
      .filter((item) => Number.isFinite(item.sellerId)),
    limit: num(payload?.limit, 0),
    offset: num(payload?.offset, 0),
    projectionMetadata,
    semanticVersion: text(payload?.semanticVersion),
  }
}

async function call<T>(request: Promise<any>, normalize: (payload: any) => T): Promise<T> {
  try {
    return normalize(unwrap(await request))
  } catch (error) {
    throw toExplorerError(error)
  }
}

export const getExplorerCatalog = (signal?: AbortSignal): Promise<ExplorerCatalog> =>
  call(api.get('/business-web/catalog', { signal }), normalizeCatalog)

export const getExplorerContext = (signal?: AbortSignal): Promise<ExplorerContext> =>
  call(api.get('/business-web/context', { signal }), normalizeContext)

/** Запрос данных. Отмена через `signal` не считается ошибкой — ловите `isAbortError`. */
export const runExplorerQuery = (body: ExplorerQueryBody, signal?: AbortSignal): Promise<ExplorerResult> =>
  call(api.post('/business-web/query', body, { signal }), normalizeResult)
